import { useState } from "react";
import { motion } from "framer-motion";
import { PORTFOLIO } from "@/lib/data";
import { Play } from "lucide-react";
import VideoLightbox from "@/components/site/VideoLightbox";

const FILTERS = ["All", "India", "UAE", "UK"];

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [hovered, setHovered] = useState(null);
  const [activeFilm, setActiveFilm] = useState(null);

  const filtered =
    filter === "All"
      ? PORTFOLIO
      : PORTFOLIO.filter((p) => p.country === filter);

  const handleClick = (e, film) => {
    if (film.vimeo_id) {
      e.preventDefault();
      setActiveFilm(film);
    }
  };

  return (
    <section
      id="films"
      data-testid="portfolio-section"
      className="relative bg-[#050505] py-24 md:py-40 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-10">
          <span className="font-mono-label text-[10px] text-[#C4A484]">
            002 · The Films
          </span>
          <span className="flex-1 h-px bg-white/10" />
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-16">
          <h2 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-3xl">
            Eight stories.
            <br />
            Three countries.
            <br />
            <span className="italic text-[#C4A484]">One studio.</span>
          </h2>

          <div
            data-testid="portfolio-filters"
            className="flex flex-wrap gap-2"
          >
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`font-mono-label text-[10px] px-4 py-2 border transition-all duration-300 ${
                  filter === f
                    ? "border-[#C4A484] bg-[#C4A484] text-black"
                    : "border-white/20 text-[#FDFBF7]/70 hover:border-[#C4A484] hover:text-[#C4A484]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Tetris grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-12 auto-rows-[260px] md:auto-rows-[320px] gap-4 md:gap-6"
          onMouseLeave={() => setHovered(null)}
        >
          {filtered.map((p, i) => {
            const isTall = p.span === "tall";
            const spanClass = isTall
              ? "md:col-span-5 md:row-span-2"
              : "md:col-span-7 md:row-span-1";
            const dimmed = hovered && hovered !== p.id;
            return (
              <motion.a
                key={p.id}
                href={`#film-${p.id}`}
                data-testid={`portfolio-card-${p.id}`}
                onClick={(e) => handleClick(e, p)}
                onMouseEnter={() => setHovered(p.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                viewport={{ once: true }}
                className={`relative overflow-hidden group ${spanClass} transition-opacity duration-500 ${
                  dimmed ? "opacity-40" : "opacity-100"
                } ${p.vimeo_id ? "cursor-pointer" : ""}`}
              >
                <img
                  src={p.image}
                  alt={p.couple}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                {/* Country tag */}
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className="w-6 h-px bg-[#C4A484]" />
                  <span className="font-mono-label text-[10px] text-[#C4A484]">
                    {p.country}
                  </span>
                </div>

                {/* Play icon — larger + visible for films with a video */}
                <div
                  className={`absolute top-5 right-5 w-10 h-10 rounded-full border flex items-center justify-center backdrop-blur-sm transition-all duration-500 ${
                    p.vimeo_id
                      ? "border-[#C4A484] bg-black/40 opacity-90 group-hover:bg-[#C4A484] group-hover:scale-110"
                      : "border-white/30 opacity-0 group-hover:opacity-100"
                  }`}
                >
                  <Play
                    size={14}
                    className={`ml-0.5 transition-colors duration-500 ${
                      p.vimeo_id
                        ? "text-[#C4A484] group-hover:text-black"
                        : "text-white"
                    }`}
                  />
                </div>

                {/* Bottom content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-heading text-3xl md:text-4xl tracking-tighter text-[#FDFBF7] mb-1">
                        {p.couple}
                      </h3>
                      <p className="font-mono-label text-[10px] text-[#8F8F8F]">
                        {p.location} · {p.year}
                      </p>
                    </div>
                    <span className="hidden md:block font-mono-label text-[10px] text-[#C4A484] pb-1">
                      {p.vimeo_id ? "Watch Film →" : "Coming Soon"}
                    </span>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
      <VideoLightbox film={activeFilm} onClose={() => setActiveFilm(null)} />
    </section>
  );
}
